import {Product} from "@/interfaces/entities/Product";
import {InventoryItem} from "@/interfaces/entities/InventoryItem";
import countryHelper from "@/util/countries";

export type CoverageType = "worldwide" | "region" | "single";

const WORLDWIDE_COUNTRY_SETS = ["WWL", "WWM", "WWW"];

/**
 * Per-row coverage category, derived client-side from the pack's own countrySet + includedCountries
 * (both server-provided per pack). Matches the server-side facet coverageType.
 */
export function coverageTypeOf(item: Product | InventoryItem): CoverageType {
    if (WORLDWIDE_COUNTRY_SETS.includes(item.countrySet ?? "")) return "worldwide";
    return (item.includedCountries ?? []).length > 1 ? "region" : "single";
}

export function getFilteredProducts<T extends Product | InventoryItem>(items: T[]) {
    const worldWideCountrySets = ["WWL", "WWM", "WWW"];
    const worldwidePacks = items.filter((p: Product | InventoryItem) => worldWideCountrySets.includes(p.countrySet ?? ""));

    const regionPacks = items.filter(
        (p: Product | InventoryItem) => (p.includedCountries ?? []).length > 1 && !worldWideCountrySets.includes(p.countrySet ?? "")
    );

    const singleCountryPacks = items.filter(
        (p: Product | InventoryItem) => (p.includedCountries ?? []).length <= 1 && !worldWideCountrySets.includes(p.countrySet ?? "")
    );

    const sortedRegionPacks = [...regionPacks].sort((a: Product | InventoryItem, b: Product | InventoryItem) =>
        (a.countrySet ?? "").localeCompare(b.countrySet ?? "")
    );

    const sortedSingleCountryPacks = [...singleCountryPacks].sort((a: Product | InventoryItem, b: Product | InventoryItem) =>
        (countryHelper.getCountryName(a.countrySet ?? "")).localeCompare(countryHelper.getCountryName(b.countrySet ?? ""))
    );

    return [
        ...(worldwidePacks.length > 0 || sortedRegionPacks.length > 0 ? [{ isHeader: true, name: "Regions" }] : []), // Add header if there are regions
        ...(worldwidePacks.length > 0 ? worldwidePacks : []),  // Worldwide comes first in the region list
        ...sortedRegionPacks,
        ...(sortedSingleCountryPacks.length > 0 ? [{ isHeader: true, name: "Single Countries" }] : []), // Add header if there are single countries
        ...sortedSingleCountryPacks
    ];
}
